import { useMutation, useQuery } from "convex/react";
import { AlertCircle, Loader2, Lock } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@repo/ui";
import { useCandidateAuthStore } from "@repo/auth";

export function CreerParcoursCandidat() {
  const { campagneId } = useParams<{ campagneId: string }>();
  const navigate = useNavigate();
  const { setToken, setParcoursId, setFirstName, setEmail: setStoreEmail } =
    useCandidateAuthStore();

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [motDePasse, setMotDePasse] = useState("");
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");

  const campagneInfo = useQuery(
    api.campagnes.obtenirInfosCampagnePublique,
    campagneId ? { campagneId: campagneId as Id<"campagnes"> } : "skip",
  );

  const creerParcours = useMutation(api.campagnes.creerParcoursDepuisCampagne);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const result = await creerParcours({
        campagneId: campagneId as Id<"campagnes">,
        motDePasse: motDePasse || undefined,
        nom: nom || undefined,
        prenom: prenom || undefined,
        email: email || undefined,
      });

      // Pré-charger le store pour que ParcoursGuard ne demande pas le mot de passe
      setToken(result.token);
      setParcoursId(result.parcoursId);
      if (!campagneInfo?.anonymatParcours) {
        setFirstName(prenom);
        setStoreEmail(email);
      }

      void navigate(`/p/${result.parcoursId}`, { state: { token: result.token } });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  if (!campagneId) {
    return <PageErreur message="Lien de campagne invalide." />;
  }

  if (campagneInfo === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (campagneInfo === null) {
    return (
      <PageErreur message="Cette campagne n'est pas disponible ou n'autorise pas la création de parcours." />
    );
  }

  const estAnonyme = campagneInfo.anonymatParcours;

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>{campagneInfo.nom}</CardTitle>
          <CardDescription>
            {estAnonyme
              ? "Vos réponses seront anonymes. Entrez le mot de passe d'accès pour commencer."
              : "Entrez vos informations pour accéder à votre parcours d'évaluation."}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {campagneInfo.motDePasseRequis && (
              <div className="space-y-2">
                <Label htmlFor="motDePasse">Mot de passe d'accès</Label>
                <Input
                  id="motDePasse"
                  name="motDePasse"
                  type="password"
                  value={motDePasse}
                  onChange={(e) => setMotDePasse(e.target.value)}
                  placeholder="Mot de passe communiqué par votre organisation"
                  autoFocus
                  required
                />
              </div>
            )}

            {!estAnonyme && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="prenom">Prénom</Label>
                  <Input
                    id="prenom"
                    name="prenom"
                    type="text"
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    placeholder="Votre prénom"
                    autoComplete="given-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom de famille</Label>
                  <Input
                    id="nom"
                    name="nom"
                    type="text"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    placeholder="Votre nom"
                    autoComplete="family-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    autoComplete="email"
                    required
                  />
                </div>
              </>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-surface bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{error}</p>
              </div>
            )}
          </CardContent>

          <CardFooter className="pt-6">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Création en cours...
                </>
              ) : (
                "Commencer l'évaluation"
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

function PageErreur({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle>Campagne indisponible</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}
